import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExtensionIcon from '@material-ui/icons/Extension';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import { COLORS } from '../../theme';
import AdminHint from '../components/AdminHint';
import AdminViewToggle, { useAdminView } from '../components/AdminViewToggle';

const API_BASE = '/planner-api';

const MODULE_CONFIGS = {
  route_planner: { icon: '\uD83D\uDDFA\uFE0F', color: '#3b82f6', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
  fleet_tracking: { icon: '\uD83D\uDE9A', color: '#22c55e', gradient: 'linear-gradient(135deg, #22c55e, #16a34a)' },
  driver_management: { icon: '\uD83D\uDC64', color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)' },
  fuel_management: { icon: '\u26FD', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
  reports: { icon: '\uD83D\uDCCA', color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899, #db2777)' },
  maintenance: { icon: '\uD83D\uDD27', color: '#64748b', gradient: 'linear-gradient(135deg, #64748b, #475569)' },
  geofencing: { icon: '\uD83D\uDCCD', color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
  alerts: { icon: '\uD83D\uDD14', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
};

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes slideInUp': {
      '0%': { opacity: 0, transform: 'translateY(20px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
  },
  header: {
    marginBottom: 22,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: 'var(--adm-header-bg, #e2f1e9)',
    border: '1px solid var(--adm-header-border, #bfe0d1)',
    borderRadius: 14,
    padding: '18px 22px',
  },
  titleIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'rgba(99,102,241,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '1.72rem',
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.78rem',
    color: COLORS.textSecondary,
    marginTop: 2,
  },
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18 },
  tableCard: { background: 'var(--adm-card, #fff)', borderRadius: 16, border: '1px solid var(--adm-card-border, rgba(0,0,0,0.05))', boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)', overflow: 'visible' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '14px 20px', fontSize: '0.66rem', fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.06))', background: 'var(--adm-thead-bg, #f8fafc)', position: 'sticky', top: 0, zIndex: 2 },
  td: { padding: '14px 20px', fontSize: '0.84rem', color: COLORS.text, borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.03))' },
  tableRow: { transition: 'background 0.15s', '&:hover': { background: 'var(--adm-hover, #f8fafc)' }, '&:last-child td': { borderBottom: 'none' } },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 18,
  },
  moduleCard: {
    background: 'var(--adm-card, #ffffff)',
    borderRadius: 16,
    border: '1px solid var(--adm-card-border, rgba(0,0,0,0.05))',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'default',
    animation: '$slideInUp 0.4s ease-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
    },
  },
  cardAccent: {
    height: 3,
  },
  cardBody: {
    padding: '20px 22px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
  },
  moduleIcon: {
    width: 52,
    height: 52,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    flexShrink: 0,
    position: 'relative',
  },
  iconGlow: {
    position: 'absolute',
    inset: -3,
    borderRadius: 17,
    opacity: 0.15,
  },
  moduleInfo: {
    flex: 1,
    minWidth: 0,
  },
  moduleName: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: COLORS.text,
    marginBottom: 3,
  },
  moduleKey: {
    fontSize: '0.68rem',
    color: COLORS.textMuted,
    fontFamily: '"SF Mono", "Fira Code", monospace',
    background: 'var(--adm-hover, #f1f5f9)',
    padding: '3px 8px',
    borderRadius: 5,
    display: 'inline-block',
  },
  moduleDesc: {
    fontSize: '0.78rem',
    color: COLORS.textSecondary,
    marginTop: 8,
    lineHeight: 1.5,
  },
  cardFooter: {
    padding: '10px 22px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: '0.68rem',
    fontWeight: 600,
    color: '#22c55e',
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 6px rgba(34,197,94,0.4)',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 300,
  },
  editBtn: {
    width: 28,
    height: 28,
    background: 'rgba(99,102,241,0.06)',
    borderRadius: 8,
    '&:hover': { background: 'rgba(99,102,241,0.14)' },
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialog: {
    background: 'var(--adm-card, #ffffff)',
    borderRadius: 16,
    width: 440,
    maxWidth: '92vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 22px 14px',
    borderBottom: '1px solid var(--adm-border, #eef2f7)',
    color: COLORS.text,
  },
  dialogHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  dialogHeaderIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'var(--adm-brand-soft, rgba(47,93,80,0.08))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogBody: {
    padding: 20,
  },
  fieldSection: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: '0.62rem',
    fontWeight: 700,
    color: 'var(--adm-text-muted, #94a3b8)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 8,
  },
  fieldInput: {
    width: '100%',
    border: '1.5px solid var(--adm-input-border, #e2e8f0)',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: '0.88rem',
    fontWeight: 600,
    color: 'var(--adm-text, #1e293b)',
    outline: 'none',
    transition: 'all 0.2s',
    background: 'var(--adm-input-bg, #f8fafc)',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: '#6366f1',
      boxShadow: '0 0 0 3px rgba(99,102,241,0.08)',
      background: 'var(--adm-input-bg, #fff)',
    },
  },
  fieldTextarea: {
    width: '100%',
    border: '1.5px solid var(--adm-input-border, #e2e8f0)',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: '0.82rem',
    color: 'var(--adm-text, #1e293b)',
    outline: 'none',
    transition: 'all 0.2s',
    background: 'var(--adm-input-bg, #f8fafc)',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: 80,
    fontFamily: 'inherit',
    '&:focus': {
      borderColor: '#6366f1',
      boxShadow: '0 0 0 3px rgba(99,102,241,0.08)',
      background: 'var(--adm-input-bg, #fff)',
    },
  },
  keyBadge: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontFamily: '"SF Mono", "Fira Code", monospace',
    background: 'var(--adm-hover, #f1f5f9)',
    color: 'var(--adm-text-dim, #64748b)',
    padding: '4px 10px',
    borderRadius: 6,
    border: '1px solid var(--adm-border, #e2e8f0)',
  },
  dialogFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '14px 20px',
    borderTop: '1px solid var(--adm-border, #e2e8f0)',
    background: 'var(--adm-hover, #f8fafc)',
  },
  cancelBtn: {
    fontSize: '0.78rem',
    textTransform: 'none',
    color: 'var(--adm-text-dim, #64748b)',
    borderColor: 'var(--adm-border, #e2e8f0)',
    borderRadius: 8,
    padding: '6px 16px',
  },
  saveBtn: {
    fontSize: '0.78rem',
    textTransform: 'none',
    background: 'var(--adm-brand, #2f5d50)',
    color: '#fff',
    borderRadius: 8,
    padding: '6px 20px',
    boxShadow: '0 2px 8px rgba(47,93,80,0.3)',
    '&:hover': { background: 'var(--adm-brand-dark, #274c41)' },
    '&:disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
  },
}));

function AdminModules({ token }) {
  const classes = useStyles();
  const [modules, setModules] = useState([]);
  const [view, setView] = useAdminView('modules', 'cards');
  const [loading, setLoading] = useState(true);
  const [editMod, setEditMod] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/modules`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setModules(await res.json());
      } catch (err) {
        console.error('Modules load error:', err);
      }
      setLoading(false);
    }
    load();
  }, []);

  function openEdit(mod) {
    setEditMod(mod);
    setEditName(mod.name);
    setEditDesc(mod.description || '');
  }

  function closeEdit() {
    setEditMod(null);
    setSaving(false);
  }

  async function handleSave() {
    if (!editMod) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/modules/${editMod.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: editName, description: editDesc }),
      });
      if (res.ok) {
        const updated = await res.json();
        setModules(prev => prev.map(m => m.id === updated.id ? updated : m));
        closeEdit();
      }
    } catch (err) {
      console.error('Module save error:', err);
    }
    setSaving(false);
  }

  if (loading) {
    return <Box className={classes.loading}><CircularProgress size={36} style={{ color: '#6366f1' }} /></Box>;
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Box className={classes.titleIcon}>
          <ExtensionIcon style={{ color: '#6366f1', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography className={classes.title}>Modulos</Typography>
          <Typography className={classes.subtitle}>{modules.length} modulos disponiveis na plataforma</Typography>
        </Box>
      </Box>

      <AdminHint
        nodes={[{ text: 'Módulos', tone: 'orange' }, { text: 'Perfil', tone: 'indigo' }, { text: 'Organização', tone: 'green' }]}
        labels={['ligam-se num', 'que aplica a']}
        note="Cada Módulo é uma peça funcional do planner. Não se ativam aqui — ligam-se dentro de um Perfil, que depois se aplica às Organizações. Nesta página só defines o nome e a descrição de cada peça."
      />

      <Box className={classes.toolbar}>
        <Typography style={{ fontSize: '0.78rem', color: 'var(--adm-brand-mid, #5a8577)', fontWeight: 600 }}>
          As pe\u00E7as ligam-se dentro de um Perfil \u2014 aqui s\u00F3 defines o nome e a descri\u00E7\u00E3o de cada uma.
        </Typography>
        <AdminViewToggle view={view} onChange={setView} />
      </Box>

      {view === 'table' ? (
        <Box className={classes.tableCard}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>M\u00F3dulo</th>
                <th className={classes.th}>Chave</th>
                <th className={classes.th}>Descri\u00E7\u00E3o</th>
                <th className={classes.th} style={{ width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {modules.map(mod => {
                const cfg = MODULE_CONFIGS[mod.key] || { icon: '\uD83D\uDCE6', color: '#64748b' };
                return (
                  <tr key={mod.id} className={classes.tableRow}>
                    <td className={classes.td}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 18 }}>{cfg.icon}</span>
                        <Typography style={{ fontWeight: 700, fontSize: '0.87rem', color: COLORS.text }}>{mod.name}</Typography>
                      </Box>
                    </td>
                    <td className={classes.td}><Typography className={classes.moduleKey}>{mod.key}</Typography></td>
                    <td className={classes.td}>
                      <Typography style={{ fontSize: '0.8rem', color: COLORS.textSecondary }}>{mod.description || '\u2014'}</Typography>
                    </td>
                    <td className={classes.td}>
                      <IconButton className={classes.editBtn} size="small" onClick={() => openEdit(mod)}>
                        <EditIcon style={{ fontSize: 15, color: 'var(--adm-brand, #2f5d50)' }} />
                      </IconButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      ) : (
      <Box className={classes.grid}>
        {modules.map((mod, i) => {
          const cfg = MODULE_CONFIGS[mod.key] || { icon: '\uD83D\uDCE6', color: '#64748b', gradient: 'linear-gradient(135deg, #64748b, #475569)' };
          return (
            <Box key={mod.id} className={classes.moduleCard} style={{ animationDelay: `${i * 0.06}s` }}>
              <Box className={classes.cardAccent} style={{ background: cfg.gradient }} />
              <Box className={classes.cardBody}>
                <Box className={classes.moduleIcon} style={{ background: `${cfg.color}10` }}>
                  <Box className={classes.iconGlow} style={{ background: cfg.color }} />
                  {cfg.icon}
                </Box>
                <Box className={classes.moduleInfo}>
                  <Typography className={classes.moduleName}>{mod.name}</Typography>
                  <Typography className={classes.moduleKey}>{mod.key}</Typography>
                  {mod.description && (
                    <Typography className={classes.moduleDesc}>{mod.description}</Typography>
                  )}
                </Box>
              </Box>
              <Box className={classes.cardFooter}>
                <Box className={classes.statusPill}>
                  <Box className={classes.statusDot} />
                  Disponivel
                </Box>
                <IconButton className={classes.editBtn} size="small" onClick={() => openEdit(mod)}>
                  <EditIcon style={{ fontSize: 14, color: '#6366f1' }} />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>
      )}

      {/* Edit Module Popup */}
      {editMod && (() => {
        const cfg = MODULE_CONFIGS[editMod.key] || { icon: '📦', color: '#64748b' };
        return (
          <Box className={classes.overlay} onClick={closeEdit}>
            <Box className={classes.dialog} onClick={e => e.stopPropagation()}>
              <Box className={classes.dialogHeader}>
                <Box className={classes.dialogHeaderTitle}>
                  <Box className={classes.dialogHeaderIcon}>
                    <EditIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text }}>
                      Editar Modulo
                    </Typography>
                    <Typography style={{ fontSize: '0.65rem', color: 'var(--adm-text-muted, #94a3b8)' }}>
                      Alterar nome e descricao do modulo
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={closeEdit} style={{ color: 'var(--adm-text-muted, #94a3b8)' }}>
                  <CloseIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              <Box className={classes.dialogBody}>
                <Box className={classes.fieldSection}>
                  <Typography className={classes.fieldLabel}>Chave (imutavel)</Typography>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>{cfg.icon}</span>
                    <Typography className={classes.keyBadge}>{editMod.key}</Typography>
                  </Box>
                </Box>

                <Box className={classes.fieldSection}>
                  <Typography className={classes.fieldLabel}>Nome</Typography>
                  <input
                    className={classes.fieldInput}
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    placeholder="Nome do modulo"
                    autoFocus
                  />
                </Box>

                <Box className={classes.fieldSection} style={{ marginBottom: 0 }}>
                  <Typography className={classes.fieldLabel}>Descricao</Typography>
                  <textarea
                    className={classes.fieldTextarea}
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                    placeholder="Descricao do modulo (opcional)"
                  />
                </Box>
              </Box>

              <Box className={classes.dialogFooter}>
                <Button variant="outlined" className={classes.cancelBtn} onClick={closeEdit}>
                  Cancelar
                </Button>
                <Button
                  className={classes.saveBtn}
                  onClick={handleSave}
                  disabled={saving || !editName.trim()}
                  startIcon={<SaveIcon style={{ fontSize: 16 }} />}
                >
                  {saving ? 'A guardar...' : 'Guardar'}
                </Button>
              </Box>
            </Box>
          </Box>
        );
      })()}
    </Box>
  );
}

export default AdminModules;
